import React from "react";
import {
  ArrowUpRight,
  Brain,
  Cpu,
  Database,
  FileText,
  GraduationCap,
  LineChart,
  ShieldCheck,
  Target,
} from "lucide-react";
import "./TpcTrainerMarnusSpotlight.css";

import marnusImage from "../../images/Marnus02.webp";

const trainerStats = [
  {
    id: "research",
    value: "PhD",
    label: "Machine Learning research in progress",
    trend: "gold",
    icon: Brain,
  },
  {
    id: "published",
    value: "Published",
    label: "Learning models applied to market phenomena",
    trend: "green",
    icon: FileText,
  },
  {
    id: "ai",
    value: "AI",
    label: "Data Science and Artificial Intelligence specialist",
    trend: "red",
    icon: Cpu,
  },
  {
    id: "market-models",
    value: "Quant",
    label: "Market movement, signal and probability research",
    trend: "green",
    icon: LineChart,
  },
];

const researchFocus = [
  "Statistical learning vs machine learning model efficiency",
  "Market movement and exploitable price behaviour",
  "Price phenomena that deviate from random distribution",
  "Evidence-led analysis of signal, noise and probability",
];

const traderApplication = [
  "Think in probabilities instead of predictions",
  "Separate repeatable market behaviour from randomness",
  "Build testable trading hypotheses",
  "Use data to challenge bias and hindsight assumptions",
];

const credentials = [
  {
    id: "phd",
    label: "Machine Learning",
    value: "PhD candidate · University of Johannesburg",
    trend: "gold",
  },
  {
    id: "msc",
    label: "Data Science & AI",
    value: "MSc · Arden University London · Cum laude",
    trend: "green",
  },
  {
    id: "award",
    label: "Academic Achiever",
    value: "Highest classification mark in postgraduate Computer Science",
    trend: "red",
  },
  {
    id: "business",
    label: "Financial Management",
    value: "Master of Business Leadership · UNISA SBL",
    trend: "green",
  },
];

const marketCandles = [
  "green",
  "green",
  "red",
  "green",
  "gold",
  "red",
  "green",
  "green",
  "red",
  "green",
];

function TpcTrainerMarnusSpotlight({
  applyHref = "https://andus.co.za/trade-register",
  curriculumHref = "#tpc-curriculum-explorer",
}) {
  return (
    <section
      id="tpc-trainer-marnus-spotlight"
      className="trpc-trainerMarnus"
      aria-labelledby="trpc-trainerMarnus-title"
    >
      <div className="trpc-trainerMarnus__marketLayer" aria-hidden="true">
        <span className="trpc-trainerMarnus__priceLine trpc-trainerMarnus__priceLine--one" />
        <span className="trpc-trainerMarnus__priceLine trpc-trainerMarnus__priceLine--two" />

        <div className="trpc-trainerMarnus__candleGroup">
          {marketCandles.map((trend, index) => (
            <span
              key={`${trend}-${index}`}
              className="trpc-trainerMarnus__candle"
              data-trend={trend}
            />
          ))}
        </div>
      </div>

      <div className="trpc-trainerMarnus__shell">
        <div className="trpc-trainerMarnus__bridge" aria-hidden="true">
          <span>Trainer Profile 02</span>
          <i />
          <strong>Quantitative market research</strong>
        </div>

        <div className="trpc-trainerMarnus__grid">
          <div className="trpc-trainerMarnus__copy">
            <div className="trpc-trainerMarnus__terminalTop" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>

            <span className="trpc-trainerMarnus__eyebrow">
              Quantitative Trading Intelligence
            </span>

            <div className="trpc-trainerMarnus__nameBlock">
              <h2 id="trpc-trainerMarnus-title">
                Marnus Janse van Rensburg
              </h2>

              <p>CTO · Machine Learning Engineer · AI Productivity Specialist</p>
            </div>

            <div className="trpc-trainerMarnus__bio">
              <p>
                Marnus brings the quantitative edge behind TPC’s trading
                education. His work focuses on how statistical learning and
                machine learning models can analyse market movement, test price
                behaviour and identify where market phenomena may hold exploitable
                structure.
              </p>

              <p>
                His research examines the efficiency of statistical-learning
                models versus machine-learning models in analysing market
                movements, especially where price action deviates meaningfully
                from random distribution. For traders, this sharpens the ability
                to separate signal from noise, think in probabilities and build
                evidence-led trading processes.
              </p>
            </div>

            <div
              className="trpc-trainerMarnus__stats"
              aria-label="Marnus trading research highlights"
            >
              {trainerStats.map((item) => {
                const Icon = item.icon;

                return (
                  <article
                    key={item.id}
                    className="trpc-trainerMarnus__stat"
                    data-trend={item.trend}
                  >
                    <span
                      className="trpc-trainerMarnus__statIcon"
                      aria-hidden="true"
                    >
                      <Icon size={19} strokeWidth={2.35} />
                    </span>

                    <div>
                      <strong>{item.value}</strong>
                      <span>{item.label}</span>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="trpc-trainerMarnus__lowerGrid">
              <div className="trpc-trainerMarnus__researchPanel">
                <div className="trpc-trainerMarnus__panelHead">
                  <span
                    className="trpc-trainerMarnus__panelIcon"
                    aria-hidden="true"
                  >
                    <Database size={18} strokeWidth={2.35} />
                  </span>

                  <h3>Research focus</h3>
                </div>

                <ul>
                  {researchFocus.map((item) => (
                    <li key={item}>
                      <span aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="trpc-trainerMarnus__applicationPanel">
                <div className="trpc-trainerMarnus__panelHead">
                  <span
                    className="trpc-trainerMarnus__panelIcon"
                    aria-hidden="true"
                  >
                    <Target size={18} strokeWidth={2.35} />
                  </span>

                  <h3>Trader application</h3>
                </div>

                <ul>
                  {traderApplication.map((item) => (
                    <li key={item}>
                      <span aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="trpc-trainerMarnus__credentialPanel">
              <div className="trpc-trainerMarnus__panelHead">
                <span
                  className="trpc-trainerMarnus__panelIcon"
                  aria-hidden="true"
                >
                  <GraduationCap size={18} strokeWidth={2.35} />
                </span>

                <h3>Academic and technical background</h3>
              </div>

              <div className="trpc-trainerMarnus__credentialList">
                {credentials.map((item) => (
                  <article
                    key={item.id}
                    className="trpc-trainerMarnus__credential"
                    data-trend={item.trend}
                  >
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </article>
                ))}
              </div>
            </div>

            <div className="trpc-trainerMarnus__trustStrip">
              <span
                className="trpc-trainerMarnus__trustIcon"
                aria-hidden="true"
              >
                <ShieldCheck size={18} strokeWidth={2.35} />
              </span>

              <p>
                Marnus helps learners understand the analytical side of trading:
                how markets can be researched, how assumptions can be tested and
                why disciplined evidence matters before risking capital.
              </p>
            </div>

            <div className="trpc-trainerMarnus__actions">
              <a
                className="trpc-trainerMarnus__btn trpc-trainerMarnus__btn--primary"
                href={applyHref}
                target="_blank"
                rel="noopener noreferrer"
                data-candle="green"
              >
                <span>Train with TPC</span>
                <ArrowUpRight size={17} strokeWidth={2.4} aria-hidden="true" />
              </a>

              <a
                className="trpc-trainerMarnus__btn trpc-trainerMarnus__btn--secondary"
                href={curriculumHref}
                data-candle="red"
              >
                <span>View Curriculum</span>
              </a>
            </div>
          </div>

          <div
            className="trpc-trainerMarnus__visual"
            aria-label="Marnus Janse van Rensburg"
          >
            <div className="trpc-trainerMarnus__portraitStage">
              <div
                className="trpc-trainerMarnus__portraitTerminalTop"
                aria-hidden="true"
              >
                <span />
                <span />
                <span />
              </div>

              <div className="trpc-trainerMarnus__marketPlate" aria-hidden="true">
                <span>Machine Learning</span>
                <strong>AI</strong>
              </div>

              <div className="trpc-trainerMarnus__modelMap" aria-hidden="true">
                <span className="node node--one" />
                <span className="node node--two" />
                <span className="node node--three" />
                <span className="node node--four" />
                <i className="line line--one" />
                <i className="line line--two" />
                <i className="line line--three" />
              </div>

              <div className="trpc-trainerMarnus__portraitChart" aria-hidden="true">
                {marketCandles.slice(0, 8).map((trend, index) => (
                  <span key={`${trend}-marnus-${index}`} data-trend={trend} />
                ))}
              </div>

              <div className="trpc-trainerMarnus__subjectFrame">
                <img
                  className="trpc-trainerMarnus__portrait"
                  src={marnusImage}
                  alt="Marnus Janse van Rensburg"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div className="trpc-trainerMarnus__visualBadge trpc-trainerMarnus__visualBadge--top">
                <span>Research area</span>
                <strong>Market movement models</strong>
              </div>

              <div className="trpc-trainerMarnus__visualBadge trpc-trainerMarnus__visualBadge--bottom">
                <span>Trading lens</span>
                <strong>Signal vs randomness</strong>
              </div>

              <div className="trpc-trainerMarnus__visualBadge trpc-trainerMarnus__visualBadge--side">
                <span>Role</span>
                <strong>TPC CTO</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TpcTrainerMarnusSpotlight;